import { Button, Form, Input, message } from "antd";
import { signInWithPopup } from "firebase/auth";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { socialLogin, userSignIn } from "../../../../controller/services/auth";
import { auth, provider } from "../../../../firebase";
import useFormValidation from "../../../../hooks/useFormValid";
import i18n from "../../../../localization/i18n";
import {
  setInfoContent,
  setInnerStepper,
  setLoading,
  setStepperStatus,
} from "../../../../redux/slices/common_slice";
import { setUserFormData } from "../../../../redux/slices/form_slice";
import { setUserData } from "../../../../redux/slices/user_slice";
import { routePaths } from "../../../../routing/routePaths";
import { getApiErrorMessage, isPWA } from "../../../../utils/helper";
import LoginSignupWrapper from "../LoginSignupWrapper";

const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || "1204776367620016";

const Login = () => {
  const [form] = Form.useForm();
  const { formValid, handleValuesChange, formValues } = useFormValidation(form);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [fbAuthType, setFbAuthType] = useState();
  const fcmToken = Cookies.get("fcmToken");
  useEffect(() => {
    if (!isPWA()) {
      localStorage.clear();
    }
  }, []);
  const onFinish = (values) => {
    dispatch(setLoading(true));
    const payload = {
      loginType: "email",
      fcmToken: fcmToken,
      email: values.username,
      password: values.password,
    };
    userSignIn(payload)
      .then((resp) => {
        localStorage.setItem("token", resp.data.token);
        dispatch(setUserData(resp.data.existUser));
        dispatch(setUserFormData(resp.data.existUser));
        dispatch(setStepperStatus(resp.data.status));
        handleRouting(resp.data.status);
        message.success(resp?.message?.[i18n.language]);
      })
      .catch((err) => {
        message.error(getApiErrorMessage(err, t("errors.socialLoginFailed")));
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handleRouting = (statusData) => {
    if (statusData.isExpenseCompleted) {
      navigate("/dashboard");
    } else if (statusData?.isIncomeCompleted) {
      navigate(routePaths.expenses);
      dispatch(setInnerStepper(statusData?.expenseStep || 0));
    } else if (statusData?.isProfileCompleted) {
      navigate(routePaths.income);
      dispatch(setInnerStepper(statusData?.incomeStep || 0));
    } else if (statusData?.profileStep) {
      navigate(routePaths.onboarding);
      dispatch(setInnerStepper(statusData?.profileStep || 0));
    } else {
      navigate(routePaths.getStarted);
    }
  };

  const signin = () => {
    dispatch(setLoading(true));
    signInWithPopup(auth, provider)
      .then((resp) => {
        dispatch(setLoading(true));
        const email = resp?.user?.email?.trim().toLowerCase();
        const googleId = resp?.user?.uid;
        if (!email || !googleId) {
          message.error(t("errors.socialLoginFailed"));
          dispatch(setLoading(false));
          return;
        }
        const payload = {
          loginType: "google",
          email: email,
          googleId: googleId,
        };
        if (resp?.user?.displayName) {
          payload.fullName = resp.user.displayName;
        }
        if (fcmToken) {
          payload.fcmToken = fcmToken;
        }
        socialLogin(payload)
          .then((respo) => {
            localStorage.setItem("token", respo.data.token);
            dispatch(setUserData(respo.data?.socialUserData));
            dispatch(setUserFormData(respo.data?.socialUserData));
            dispatch(setStepperStatus(respo.data.status));
            handleRouting(respo.data.status);
            message.success(respo?.message?.[i18n.language]);
          })
          .catch((err) => {
            message.error(
              getApiErrorMessage(err, t("errors.socialLoginFailed"))
            );
          })
          .finally(() => {
            dispatch(setLoading(false));
          });
      })
      .catch((err) => {
        dispatch(setLoading(false));
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const signinWithFb = ({ email, facebookId, fullName }) => {
    dispatch(setLoading(true));
    const payload = {
      loginType: "facebook",
      email: email,
      facebookId: facebookId,
    };
    if (fullName) {
      payload.fullName = fullName;
    }
    if (fcmToken) {
      payload.fcmToken = fcmToken;
    }
    socialLogin(payload)
      .then((respo) => {
        localStorage.setItem("token", respo.data.token);
        dispatch(setUserData(respo.data?.socialUserData));
        dispatch(setUserFormData(respo.data.socialUserData));
        dispatch(setStepperStatus(respo.data.status));
        handleRouting(respo.data.status);
        message.success(respo?.message?.[i18n.language]);
      })
      .catch((err) => {
        message.error(getApiErrorMessage(err, t("errors.socialLoginFailed")));
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const responseFacebook = (response) => {
    if (response?.status === "unknown" || !response?.id) {
      dispatch(setInfoContent(t("errors.fbLogin")));
      return;
    }
    const email = response?.email?.trim().toLowerCase();
    if (!email) {
      // Facebook omits email for phone-only accounts or when the user
      // declined the email permission; the API requires it, so don't call.
      if (fbAuthType === "rerequest") {
        dispatch(setInfoContent(t("errors.fbNoEmail")));
      } else {
        setFbAuthType("rerequest");
        dispatch(setInfoContent(t("errors.fbEmailPermission")));
      }
      return;
    }
    signinWithFb({
      email: email,
      facebookId: response.id,
      fullName: response?.name,
    });
  };

  // react-facebook-login@4.1.1 has no authType prop, so a second click would
  // not re-prompt for the declined email permission; call FB.login with
  // auth_type=rerequest ourselves (still inside the user's click gesture).
  const handleFbEmailRerequest = (renderProps) => {
    if (!window.FB) {
      renderProps?.onClick?.();
      return;
    }
    window.FB.login(
      (resp) => {
        if (resp?.authResponse) {
          window.FB.api("/me", { fields: "name,email" }, (me) =>
            responseFacebook({ id: resp.authResponse.userID, ...me })
          );
        } else {
          dispatch(setInfoContent(t("errors.fbLogin")));
        }
      },
      {
        scope: "public_profile,email",
        auth_type: "rerequest",
        return_scopes: true,
      }
    );
  };
  return (
    <LoginSignupWrapper>
      <div className="row auth-details-inner-row">
        <div className="col-12 auth-details-col-9-p">
          <a href="#">
            <img
              src="/assets/images/butterfly-logo.svg"
              alt=""
              className="img-fluid logo-auth"
            />
          </a>
          <h6>{t("hello")}</h6>
          <h1>{t("signinDesc")}</h1>
          <h6>{t("signinDesc2")}</h6>
          <Form
            className="common-form"
            name="basic"
            initialValues={{
              username: "",
              password: "",
            }}
            onValuesChange={handleValuesChange}
            onFinish={onFinish}
            autoComplete="off"
            validateTrigger="onFocus"
            form={form}
          >
            <Form.Item
              label={false}
              name="username"
              rules={[
                {
                  required: true,
                  message: t("errors.email"),
                },
                {
                  type: "email",
                  message: t("errors.validEmail"),
                },
              ]}
            >
              <Input
                prefix={
                  <img
                    alt=""
                    className="img-fluid"
                    src={
                      formValues?.username
                        ? "/assets/images/icons/message-icon-primary.svg"
                        : "/assets/images/icons/message-icon.svg"
                    }
                  />
                }
                placeholder={t("enterEmail")}
              />
            </Form.Item>

            <Form.Item
              label={false}
              name="password"
              rules={[
                {
                  required: true,
                  message: t("errors.password"),
                },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-={}\[\]:;"'<>,./?|\\])[A-Za-z\d!@#$%^&*()_+\-={}\[\]:;"'<>,./?|\\]{8,}$/,
                  message: t("errors.validPass"),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value.length <= 50) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("errors.maxLimit")));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={
                  <img
                    alt=""
                    className="img-fluid"
                    src={
                      formValues?.password
                        ? "/assets/images/icons/lock-icon-primary.svg"
                        : "/assets/images/icons/lock-icon.svg"
                    }
                  />
                }
                iconRender={(visible) =>
                  visible ? (
                    <img
                      alt=""
                      className="img-fluid"
                      src="/assets/images/icons/eye-icon-primary.svg"
                    />
                  ) : (
                    <img
                      alt=""
                      className="img-fluid"
                      src="/assets/images/icons/hide-eye-icon.svg"
                    />
                  )
                }
                placeholder={t("signinEnterPassword")}
              />
            </Form.Item>

            <Form.Item valuePropName="checked">
              <h5 className="forgot-password mb-0">
                <Link to={routePaths.forgotPassword}>{t("signinForget")}</Link>
              </h5>
            </Form.Item>

            <Form.Item>
              <Button
                className="auth-theme-btn auth-login-btn"
                type="primary"
                htmlType="submit"
                disabled={
                  !formValues.username || !formValues.password || !formValid
                }
              >
                {t("signin")}
              </Button>
            </Form.Item>
            <div className="row bottom-details-login">
              <div className="col-12">
                <h6>
                  <span>{t("orSigninWith")}</span>
                </h6>
                <ul className="list-inline">
                  <li className="list-inline-item">
                    <Button
                      className="google-fb-login-btn"
                    >
                      <FacebookLogin
                        appId={FB_APP_ID}
                        fields="name,email,picture"
                        scope="public_profile,email"
                        callback={responseFacebook}
                        render={(renderProps) => (
                          <img
                            alt="facebook icon"
                            className="img-fluid"
                            onClick={
                              fbAuthType === "rerequest"
                                ? () => handleFbEmailRerequest(renderProps)
                                : renderProps?.onClick
                            }
                            src="/assets/images/icons/facebook-logo.svg"
                          />
                        )}
                        disableMobileRedirect={true}
                      />
                    </Button>
                  </li>
                  <li className="list-inline-item">
                    <Button className="google-fb-login-btn" onClick={signin}>
                      <img
                        alt="google icon"
                        className="img-fluid"
                        src="/assets/images/icons/google-logo.svg"
                      />
                    </Button>
                  </li>
                </ul>
                <p>
                  {t("!account")}{" "}
                  <Link to={routePaths.signUp}>{t("signup")}</Link>
                </p>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </LoginSignupWrapper>
  );
};

export default Login;
